import { PDFNote } from '../models/PDFNote';
import { PDFNoteQuery } from '../models/PDFNoteQuery';
import { NoteUpdatesController, normalizeNoteIds } from './noteUpdates';

interface Options {
  query: PDFNoteQuery;
  attachmentId: number;
  noteUpdates: NoteUpdatesController;
  request: (query: PDFNoteQuery) => Promise<{ Items: PDFNote[]; TotalCount: number }>;
  onNotes: (notes: PDFNote[]) => void;
  onPending: (pending: boolean) => void;
  onError: (error: unknown) => void;
}

const belongsToAttachment = (note: PDFNote, attachmentId: number) =>
  (note.NoteWorkflowInstanceAttachments ?? []).some(item =>
    item.PDFWorkflowInstanceAttachment?.ProjectDocFolderAttachmentId === attachmentId);

/** Предел старого FetchPaged в контрагентском API. */
const CHANGED_NOTES_BATCH_SIZE = 20;

export function createContractorViewerNoteUpdates(options: Options) {
  let active = false;
  let generation = 0;
  let notes: PDFNote[] | undefined;
  let running: Promise<void> | undefined;
  let failed = false;
  let mutationPending = false;
  const pending = new Set<number>();
  const changed = new Set<number>();

  const publish = () => {
    if (!changed.size) return;
    const ids = [...changed];
    changed.clear();
    options.noteUpdates.publish(ids);
  };

  const isPending = () => mutationPending || failed || !!running || pending.size > 0;

  const read = async (ids: number[], isCurrent: () => boolean) => {
    for (let attempt = 0; ; attempt += 1) {
      const response = await options.request({ ...options.query, Ids: ids, Skip: 0, Take: ids.length });
      if (!isCurrent()) return [];
      const returnedIds = response.Items.map(note => note.Id);
      if (response.TotalCount === response.Items.length &&
        new Set(returnedIds).size === returnedIds.length &&
        returnedIds.every(id => id !== undefined && ids.includes(id))) return response.Items;
      if (attempt) throw new Error('Не удалось получить согласованное состояние замечаний.');
    }
  };

  const drain = (): Promise<void> => {
    if (!active || !notes || failed || !pending.size) return running ?? Promise.resolve();
    if (running) return running;
    const currentGeneration = generation;
    const isCurrent = () => active && generation === currentGeneration;
    options.onPending(true);
    running = Promise.resolve().then(async () => {
      while (isCurrent() && pending.size) {
        const ids = [...pending].slice(0, CHANGED_NOTES_BATCH_SIZE);
        ids.forEach(id => pending.delete(id));
        try {
          const items = await read(ids, isCurrent);
          if (!isCurrent()) return;
          const replacements = new Map(items.filter(note => belongsToAttachment(note, options.attachmentId))
            .map(note => [note.Id!, note]));
          const next: PDFNote[] = [];
          for (const note of notes!) {
            if (!ids.includes(note.Id!)) next.push(note);
            else if (replacements.has(note.Id!)) {
              next.push(replacements.get(note.Id!)!);
              replacements.delete(note.Id!);
            }
          }
          next.push(...replacements.values());
          notes = next;
          options.onNotes(next);
        } catch (error) {
          if (!isCurrent()) return;
          ids.forEach(id => pending.add(id));
          failed = true;
          options.onError(error);
          return;
        }
      }
    }).finally(() => {
      if (!isCurrent()) return;
      running = undefined;
      options.onPending(isPending());
      if (!failed && pending.size) void drain();
    });
    return running;
  };

  return {
    activate() {
      active = true;
    },
    deactivate() {
      active = false;
      generation += 1;
      running = undefined;
      notes = undefined;
      pending.clear();
      failed = false;
      mutationPending = false;
      publish();
    },
    acceptInitial(items: PDFNote[]) {
      if (!active) return;
      notes = items;
      void drain();
    },
    changed(ids: readonly number[]) {
      const normalized = normalizeNoteIds(ids);
      normalized.forEach(id => changed.add(id));
      if (!active) {
        // Запрос изменения мог завершиться уже после закрытия документа.
        publish();
        return Promise.resolve();
      }
      normalized.forEach(id => pending.add(id));
      return drain();
    },
    retry() {
      failed = false;
      return drain();
    },
    beginMutation() {
      if (!active || isPending()) return false;
      mutationPending = true;
      options.onPending(true);
      return true;
    },
    finishMutation() {
      mutationPending = false;
      if (active) options.onPending(isPending());
    },
    isPending,
  };
}

export type ContractorViewerNoteUpdates = ReturnType<typeof createContractorViewerNoteUpdates>;
